import {
  AppSessionCell,
  AppSessionCellId,
  AppSessionId,
  CellId,
  DateTimeString,
  DisplayTableConfigId,
  ExploreSpecSessionStateId,
  ExportTypeAndStatus,
  HexVersionId,
  KernelId,
  OutputContent,
  OutputContentId,
  OutputId,
  ParameterId,
  ParameterValueId,
  ScopeItem,
  SharedFilterSessionStateId,
  asciiCompare,
  getDateTimeString,
  getExportTypesAndStatus,
  notEmpty,
  stableEmptyArray,
  typedObjectEntries,
  typedObjectFromEntries,
  typedObjectValues,
} from "@hex/common";
import {
  EntityState,
  PayloadAction,
  createDraftSafeSelector,
  createEntityAdapter,
  createReducer,
  createSlice,
} from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { memoize, omitBy } from "lodash";
import { shallowEqual } from "react-redux";
import { createSelector } from "reselect";

import {
  AppSessionCellMpFragment,
  AppSessionFileMpFragment,
  AppSessionMpFragment,
  KernelMpFragment,
  ParameterValueMpFragment,
  ScopeItemFragment,
} from "../../appsession-multiplayer/AppSessionMPModel.generated";
import { DisplayTableFilterStates } from "../../components/saved-views/useGetViewSelectors.js";
import { cachedMergeExploreSpecWithSessionState } from "../../explore-v2/hooks/exploreCellContentsStateHooks.js";
import { RootState } from "../store";
import { createShallowResultSelector } from "../utils/createShallowSelector";
import {
  getSelectorsForEntityState,
  getSelectorsForEntityStateWithSoftDelete,
} from "../utils/entityAdapterSelectorCreator";
import { KeysOfUnion, assertNonNull } from "../utils/types";

import {
  OutputWithContent,
  appSessionEventStateSelectors,
} from "./appSessionEventSlice";
import { hexVersionMPSelectors } from "./hexVersionMPSlice";
import {
  OutputContentLoadStatus,
  OutputContentValue,
  outputContentSelectors,
} from "./outputContentSlice";

//#region action helper types
type WithAppSessionId<T> = {
  appSessionId: AppSessionId;
  data: T;
};
type AppSessionAction<T> = PayloadAction<WithAppSessionId<T>>;
type SetFieldAction<T> = AppSessionAction<{
  key: keyof NonNullable<T>;
  value: unknown;
}>;
type SetEntityFieldPayload<T extends { id: string }> = {
  entityId: T["id"];
  key: keyof NonNullable<T>;
  value: unknown;
};
type SetEntityFieldAction<T extends { id: string }> = AppSessionAction<
  SetEntityFieldPayload<T>
>;

//#region types of data in redux store

export type AppSessionCellMP = Omit<
  AppSessionCellMpFragment,
  | "chartCellSessionState"
  | "displayTableSessionState"
  | "filterCellSessionState"
  | "exploreSpecSessionState"
  | "outputs"
>;

export type OutputContentMP = Omit<
  AppSessionCellMpFragment["outputs"][number]["frontendOutputContents"][number],
  "requiresLazyLoad" | "contents"
>;
export type ResolvedOutputContentMP = OutputContentMP & OutputContentValue;

export type OutputMP = Omit<
  AppSessionCellMpFragment["outputs"][number],
  "frontendOutputContents"
> & {
  frontendOutputContents: OutputContentMP[];
};
export type OutputMPWithResolvedContent = Omit<
  OutputMP,
  "frontendOutputContents"
> & {
  frontendOutputContents: ResolvedOutputContentMP[];
  exportTypesAndStatus: ExportTypeAndStatus[];
};

export type ChartCellSessionStateMP = NonNullable<
  AppSessionCellMpFragment["chartCellSessionState"]
>;
export type DisplayTableSessionStateMP = NonNullable<
  AppSessionCellMpFragment["displayTableSessionState"]
>;
export type FilterCellSessionStateMP = NonNullable<
  AppSessionCellMpFragment["filterCellSessionState"]
>;
export type ExploreSpecSessionStateMP = NonNullable<
  AppSessionCellMpFragment["exploreSpecSessionState"]
>;
export type OutputsStateMP = NonNullable<OutputMP>;
export type OutputContentsStateMP = NonNullable<OutputContentMP>;
export type KernelMP = KernelMpFragment;
export type ParameterValueMP = ParameterValueMpFragment;
export type ScopeItemMP = ScopeItemFragment;
export type SharedFilterSessionStateMP = NonNullable<
  AppSessionMpFragment["sharedFilterSessionStates"]
>[number];
export type AppSessionMP = Omit<
  AppSessionMpFragment,
  "appSessionCells" | "kernel" | "parameterValues" | "scope" | "files"
>;
export type AppSessionFileMP = AppSessionFileMpFragment;

//#region adapters for normalization of lists of elements
const appSessionCellAdapter = createEntityAdapter<AppSessionCellMP>({
  selectId: (cell) => cell.id,
  sortComparer: (a, b) => asciiCompare(a.id, b.id),
});
const chartCellSessionStateAdapter =
  createEntityAdapter<ChartCellSessionStateMP>({
    // unlike most of our types, we don't key off of the direct id
    selectId: (table) => table.appSessionCellId,
  });
const displayTableSessionStateAdapter =
  createEntityAdapter<DisplayTableSessionStateMP>({
    // unlike most of our types, we don't key off of the direct id
    selectId: (table) => table.appSessionCellId,
  });
const filterCellSessionStateAdapter =
  createEntityAdapter<FilterCellSessionStateMP>({
    // unlike most of our types, we don't key off of the direct id
    selectId: (table) => table.appSessionCellId,
  });
const exploreSpecSessionStateAdapter =
  createEntityAdapter<ExploreSpecSessionStateMP>({
    selectId: (exploreSpecSessionState) => exploreSpecSessionState.id,
  });
const outputStateAdapter = createEntityAdapter<OutputsStateMP>({
  selectId: (output) => output.id,
});
const parameterValueAdapter = createEntityAdapter<ParameterValueMP>({
  selectId: (par) => par.id,
  sortComparer: (a, b) => asciiCompare(a.id, b.id),
});
const scopeAdapter = createEntityAdapter<ScopeItemMP>({
  selectId: (scope) => scope.name,
  sortComparer: (a, b) => asciiCompare(a.name, b.name),
});
const sharedFilterSessionStateAdapter =
  createEntityAdapter<SharedFilterSessionStateMP>({
    selectId: (sharedFilterSessionState) => sharedFilterSessionState.id,
  });
const appSessionFilesAdapter = createEntityAdapter<AppSessionFileMP>({
  selectId: (file) => file.id,
  sortComparer: (a, b) => asciiCompare(a.id, b.id),
});

//#region reducer for data for a single AppSessionMP instance
export type AppSessionMPValue = {
  appSession: AppSessionMP;
  appSessionCells: EntityState<AppSessionCellMP>;
  chartCellSessionStates: EntityState<ChartCellSessionStateMP>;
  displayTableSessionStates: EntityState<DisplayTableSessionStateMP>;
  filterCellSessionStates: EntityState<FilterCellSessionStateMP>;
  exploreSpecSessionStates: EntityState<ExploreSpecSessionStateMP>;
  sharedFilterSessionStates: EntityState<SharedFilterSessionStateMP>;
  outputs: EntityState<OutputsStateMP>;
  kernel: KernelMP | null;
  parameterValues: EntityState<ParameterValueMP>;
  scope: EntityState<ScopeItemMP>;
  files: EntityState<AppSessionFileMP>;
  cellIdToAppSessionCellId: Record<CellId, AppSessionCellId | undefined>;
  parameterIdToParameterValueId: Record<
    ParameterId,
    ParameterValueId | undefined
  >;
  appSessionCellIdToExploreSpecSessionStateId: Record<
    AppSessionCellId,
    ExploreSpecSessionStateId
  >;
  appSessionCellIdToOutputIds: Record<AppSessionCellId, OutputId[]>;
};

const appSessionMPValueSlice = createSlice({
  name: "appSessionMPValue",
  // This initial state is essentially meaningless,
  // Since this reducer is only called as a function directly by `appSessionMPReducer` and
  // we always should have a `initializeFromAppSessionMPData` action to really initialize things.
  initialState: null as unknown as AppSessionMPValue,
  reducers: {
    initializeFromAppSessionMPData(
      _state,
      action: AppSessionAction<AppSessionMpFragment>,
    ) {
      const {
        appSessionCells: rawAppSessionCells,
        files,
        kernel,
        parameterValues,
        scope,
        ...appSessionData
      } = action.payload.data;

      const appSessionCells = rawAppSessionCells.map(
        ({
          chartCellSessionState,
          displayTableSessionState,
          exploreSpecSessionState,
          filterCellSessionState,
          outputs,
          ...cell
        }) => cell,
      );

      const chartCellSessionStates = rawAppSessionCells.flatMap((cell) =>
        cell.chartCellSessionState == null ? [] : [cell.chartCellSessionState],
      );

      const displayTableSessionStates = rawAppSessionCells.flatMap((cell) =>
        cell.displayTableSessionState == null
          ? []
          : [cell.displayTableSessionState],
      );

      const filterCellSessionStates = rawAppSessionCells.flatMap((cell) =>
        cell.filterCellSessionState == null
          ? []
          : [cell.filterCellSessionState],
      );

      const exploreSpecSessionStates = rawAppSessionCells.flatMap((cell) =>
        cell.exploreSpecSessionState == null
          ? []
          : [cell.exploreSpecSessionState],
      );

      const outputStates: OutputsStateMP[] = rawAppSessionCells
        .flatMap((cell) => cell.outputs)
        .map((output) => {
          return {
            ...output,
            frontendOutputContents: output.frontendOutputContents.map((c) => {
              const {
                contents: _contents,
                requiresLazyLoad: _requiresLazyLoad,
                ...rest
              } = c;
              return rest;
            }),
          };
        });

      return {
        appSession: appSessionData,
        appSessionCells: appSessionCellAdapter.setAll(
          appSessionCellAdapter.getInitialState(),
          appSessionCells,
        ),
        chartCellSessionStates: chartCellSessionStateAdapter.setAll(
          chartCellSessionStateAdapter.getInitialState(),
          chartCellSessionStates,
        ),
        displayTableSessionStates: displayTableSessionStateAdapter.setAll(
          displayTableSessionStateAdapter.getInitialState(),
          displayTableSessionStates,
        ),
        filterCellSessionStates: filterCellSessionStateAdapter.setAll(
          filterCellSessionStateAdapter.getInitialState(),
          filterCellSessionStates,
        ),
        exploreSpecSessionStates: exploreSpecSessionStateAdapter.setAll(
          exploreSpecSessionStateAdapter.getInitialState(),
          exploreSpecSessionStates,
        ),
        outputs: outputStateAdapter.setAll(
          outputStateAdapter.getInitialState(),
          outputStates,
        ),
        kernel: kernel,
        parameterValues: parameterValueAdapter.setAll(
          parameterValueAdapter.getInitialState(),
          parameterValues,
        ),
        scope: scopeAdapter.setAll(scopeAdapter.getInitialState(), scope),
        exploreDialog: {
          showSql: false,
        },
        sharedFilterSessionStates: sharedFilterSessionStateAdapter.setAll(
          sharedFilterSessionStateAdapter.getInitialState(),
          appSessionData.sharedFilterSessionStates,
        ),
        files: appSessionFilesAdapter.setAll(
          appSessionFilesAdapter.getInitialState(),
          files ?? [],
        ),
        cellIdToAppSessionCellId: appSessionCells.reduce<
          Record<CellId, AppSessionCellId>
        >((acc, curr) => {
          acc[curr.cellId] = curr.id;
          return acc;
        }, {}),
        appSessionCellIdToExploreSpecSessionStateId:
          exploreSpecSessionStates.reduce<
            Record<AppSessionCellId, ExploreSpecSessionStateId>
          >((acc, curr) => {
            acc[curr.appSessionCellId] = curr.id;
            return acc;
          }, {}),
        appSessionCellIdToOutputIds: outputStates.reduce<
          Record<AppSessionCellId, OutputId[]>
        >((acc, curr) => {
          (acc[curr.appSessionCellId] ??= []).push(curr.id);
          return acc;
        }, {}),
        parameterIdToParameterValueId: parameterValues.reduce<
          Record<ParameterId, ParameterValueId>
        >((acc, curr) => {
          acc[curr.parameterId] = curr.id;
          return acc;
        }, {}),
      };
    },
    // app session actions
    setAppSessionField(state, action: SetFieldAction<AppSessionMP>) {
      const appSession: AppSessionMP = state.appSession;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (appSession as Record<string, any>)[action.payload.data.key] =
        action.payload.data.value;
    },
    // app session cell actions
    upsertAppSessionCell(state, action: AppSessionAction<AppSessionCellMP>) {
      const { cellId, id } = action.payload.data;

      const prevAppSessionCell = state.appSessionCells.entities[id];

      appSessionCellAdapter.upsertOne(
        state.appSessionCells,
        action.payload.data,
      );

      state.cellIdToAppSessionCellId[cellId] = id;

      if (prevAppSessionCell == null) {
        state.appSessionCellIdToOutputIds[id] = [];
      }
    },
    setAppSessionCellField(
      state,
      { payload: { data } }: SetEntityFieldAction<AppSessionCellMP>,
    ) {
      if (data.key === "cellId") {
        const prev = state.appSessionCells.entities[data.entityId];
        if (prev != null) {
          delete state.cellIdToAppSessionCellId[prev.cellId];
          state.cellIdToAppSessionCellId[data.value as CellId] = data.entityId;
        }
      }
      appSessionCellAdapter.updateOne(state.appSessionCells, {
        id: data.entityId,
        changes: { [data.key]: data.value },
      });
    },
    deleteAppSessionCell(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        appSessionCellId: AppSessionCellId;
        deletedDate: DateTimeString;
      }>,
    ) {
      appSessionCellAdapter.updateOne(state.appSessionCells, {
        id: data.appSessionCellId,
        changes: {
          deletedDate: data.deletedDate,
        },
      });
    },
    upsertOutput(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        output: OutputMP;
      }>,
    ) {
      outputStateAdapter.upsertOne(state.outputs, data.output);

      const appSessionCellInfo = (state.appSessionCellIdToOutputIds[
        data.output.appSessionCellId
      ] ??= []);
      if (!appSessionCellInfo.includes(data.output.id)) {
        appSessionCellInfo.push(data.output.id);
      }
    },
    replaceOutputContents(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        outputId: OutputId;
        contents: OutputContentMP[];
      }>,
    ) {
      outputStateAdapter.updateOne(state.outputs, {
        id: data.outputId,
        changes: {
          frontendOutputContents: [...data.contents],
        },
      });
    },
    hardDeleteOutput(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        outputId: OutputId;
      }>,
    ) {
      const prevOutput = state.outputs.entities[data.outputId];

      outputStateAdapter.removeOne(state.outputs, data.outputId);

      if (prevOutput != null) {
        const appSessionCellInfo =
          state.appSessionCellIdToOutputIds[prevOutput.appSessionCellId];
        if (appSessionCellInfo?.includes(data.outputId)) {
          state.appSessionCellIdToOutputIds[prevOutput.appSessionCellId] =
            appSessionCellInfo.filter((id) => id !== data.outputId);
        }
      }
    },
    // chart cell session state actions
    setChartCellSessionStateField(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        appSessionCellId: AppSessionCellId;
        key: KeysOfUnion<ChartCellSessionStateMP>;
        value: unknown;
      }>,
    ) {
      chartCellSessionStateAdapter.updateOne(state.chartCellSessionStates, {
        id: data.appSessionCellId,
        changes: { [data.key]: data.value },
      });
    },
    // display table session state actions
    upsertDisplayTableSessionState(
      state,
      action: AppSessionAction<DisplayTableSessionStateMP>,
    ) {
      displayTableSessionStateAdapter.upsertOne(
        state.displayTableSessionStates,
        action.payload.data,
      );
    },
    setDisplayTableSessionStateField(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        appSessionCellId: AppSessionCellId;
        key: KeysOfUnion<DisplayTableSessionStateMP>;
        value: unknown;
      }>,
    ) {
      displayTableSessionStateAdapter.updateOne(
        state.displayTableSessionStates,
        {
          id: data.appSessionCellId,
          changes: { [data.key]: data.value },
        },
      );
    },
    deleteDisplayTableSessionState(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        appSessionCellId: AppSessionCellId;
        deletedDate: DateTimeString;
      }>,
    ) {
      displayTableSessionStateAdapter.updateOne(
        state.displayTableSessionStates,
        {
          id: data.appSessionCellId,
          changes: {
            deletedDate: data.deletedDate,
          },
        },
      );
    },
    // filter cell session state actions
    upsertFilterCellSessionState(
      state,
      action: AppSessionAction<FilterCellSessionStateMP>,
    ) {
      filterCellSessionStateAdapter.upsertOne(
        state.filterCellSessionStates,
        action.payload.data,
      );
    },
    setFilterCellSessionStateField(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        appSessionCellId: AppSessionCellId;
        key: KeysOfUnion<FilterCellSessionStateMP>;
        value: unknown;
      }>,
    ) {
      filterCellSessionStateAdapter.updateOne(state.filterCellSessionStates, {
        id: data.appSessionCellId,
        changes: { [data.key]: data.value },
      });
    },
    deleteFilterCellSessionState(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        appSessionCellId: AppSessionCellId;
        deletedDate: DateTimeString;
      }>,
    ) {
      filterCellSessionStateAdapter.updateOne(state.filterCellSessionStates, {
        id: data.appSessionCellId,
        changes: {
          deletedDate: data.deletedDate,
        },
      });
    },
    // explore spec session state actions
    upsertExploreSpecSessionState(
      state,
      action: AppSessionAction<ExploreSpecSessionStateMP>,
    ) {
      exploreSpecSessionStateAdapter.upsertOne(
        state.exploreSpecSessionStates,
        action.payload.data,
      );
      // Update index
      state.appSessionCellIdToExploreSpecSessionStateId[
        action.payload.data.appSessionCellId
      ] = action.payload.data.id;
    },
    setExploreSpecSessionStateField(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        exploreSpecSessionStateId: ExploreSpecSessionStateId;
        key: KeysOfUnion<ExploreSpecSessionStateMP>;
        value: unknown;
      }>,
    ) {
      exploreSpecSessionStateAdapter.updateOne(state.exploreSpecSessionStates, {
        id: data.exploreSpecSessionStateId,
        changes: { [data.key]: data.value },
      });
    },
    deleteExploreSpecSessionState(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        exploreSpecSessionStateId: ExploreSpecSessionStateId;
        deletedDate: DateTimeString;
      }>,
    ) {
      exploreSpecSessionStateAdapter.updateOne(state.exploreSpecSessionStates, {
        id: data.exploreSpecSessionStateId,
        changes: {
          deletedDate: data.deletedDate,
        },
      });
    },
    // shared filter session state actions
    upsertSharedFilterSessionState(
      state,
      action: AppSessionAction<SharedFilterSessionStateMP>,
    ) {
      sharedFilterSessionStateAdapter.upsertOne(
        state.sharedFilterSessionStates,
        action.payload.data,
      );
    },
    setSharedFilterSessionState(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        sharedFilterSessionStateId: SharedFilterSessionStateId;
        key: KeysOfUnion<SharedFilterSessionStateMP>;
        value: unknown;
      }>,
    ) {
      sharedFilterSessionStateAdapter.updateOne(
        state.sharedFilterSessionStates,
        {
          id: data.sharedFilterSessionStateId,
          changes: { [data.key]: data.value },
        },
      );
    },
    deleteSharedFilterSessionState(
      state,
      {
        payload: { data },
      }: AppSessionAction<{ id: SharedFilterSessionStateId }>,
    ) {
      sharedFilterSessionStateAdapter.updateOne(
        state.sharedFilterSessionStates,
        {
          id: data.id,
          changes: {
            deletedDate: getDateTimeString(new Date()),
          },
        },
      );
    },
    // kernel actions
    setKernelField(state, action: SetFieldAction<KernelMP>) {
      // initialize the kernel if it doesn't exist yet
      if (state.kernel == null) {
        state.kernel = {
          __typename: "Kernel",
          id: state.appSession.id as unknown as KernelId,
          kernelVersion: null,
          sidecarVersion: null,
          expires: null,
          kernelSize: null,
          kernelImage: null,
          kernelSourceImageId: null,
          isActiveKernelSourceVersion: null,
        };
      }

      const kernel: KernelMP = state.kernel;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (kernel as Record<string, any>)[action.payload.data.key] =
        action.payload.data.value;
    },
    // parameter value actions
    upsertParameterValue(state, action: AppSessionAction<ParameterValueMP>) {
      parameterValueAdapter.upsertOne(
        state.parameterValues,
        action.payload.data,
      );
      state.parameterIdToParameterValueId[action.payload.data.parameterId] =
        action.payload.data.id;
    },
    setParameterValueField(
      state,
      { payload: { data } }: SetEntityFieldAction<ParameterValueMP>,
    ) {
      const prev = state.parameterValues.entities[data.entityId];
      parameterValueAdapter.updateOne(state.parameterValues, {
        id: data.entityId,
        changes: { [data.key]: data.value },
      });
      if (data.key === "parameterId") {
        if (prev != null) {
          delete state.parameterIdToParameterValueId[prev.parameterId];
        }
        state.parameterIdToParameterValueId[data.value as ParameterId] =
          data.entityId;
      }
    },
    deleteParameterValue(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        parameterValueId: ParameterValueId;
        deletedDate: DateTimeString;
      }>,
    ) {
      parameterValueAdapter.updateOne(state.parameterValues, {
        id: data.parameterValueId,
        changes: {
          deletedDate: data.deletedDate,
        },
      });
    },
    // scope actions
    setAllScopeItems(state, action: AppSessionAction<ScopeItemMP[]>) {
      scopeAdapter.setAll(state.scope, action.payload.data);
    },
    upsertScopeItem(state, action: AppSessionAction<ScopeItemMP>) {
      scopeAdapter.upsertOne(state.scope, action.payload.data);
    },
    deleteScopeItem(
      state,
      {
        payload: { data },
      }: AppSessionAction<{
        scopeItemName: string;
      }>,
    ) {
      scopeAdapter.removeOne(state.scope, data.scopeItemName);
    },
    upsertFile(state, action: AppSessionAction<AppSessionFileMP>) {
      appSessionFilesAdapter.upsertOne(state.files, action.payload.data);
    },
    // file actions
    setFileField(
      state,
      { payload: { data } }: SetEntityFieldAction<AppSessionFileMP>,
    ) {
      appSessionFilesAdapter.updateOne(state.files, {
        id: data.entityId,
        changes: { [data.key]: data.value },
      });
    },
  },
});

/**
 * Given a function that converts from type INPUT to type OUTPUT,
 * uses a weak map to cache the conversion if INPUT is unchanged.
 *
 * This is particularly useful for converting data from app session events to mp
 * so that we don't accidentally cause churn from references updating
 */
function createCachedConvert<INPUT extends object, OUTPUT extends object>(
  convert: (input: INPUT) => OUTPUT,
): (input: INPUT) => OUTPUT {
  const cache = new WeakMap<INPUT, OUTPUT>();
  return (input) => {
    const prev = cache.get(input);
    if (prev != null) {
      return prev;
    }
    const next = convert(input);
    cache.set(input, next);
    return next;
  };
}

//#region selectors
// We wrap each function that creates selectors in `memoize`. This has a few benefits:
//   - We don't have to construct a whole new object of selectors each call
//   - And more importantly, you get back the same selector instances each time you call it,
//     which means that the cache for reselect selectors will work globally and thus will only
//     have to run the computation one time (instead of once per component)
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appSessionMPSelectors = {
  getAppSessionSelectors: memoize((appSessionId: AppSessionId) => {
    const safeSelect = createDraftSafeSelector(
      (state: RootState) => state.appSessionMP[appSessionId]?.appSession,
      appSessionEventStateSelectors(appSessionId).appSession.safeSelect,
      (mpAppSession, eventAppSession): AppSessionMP | undefined => {
        if (mpAppSession == null) {
          return mpAppSession;
        } else if (mpAppSession.useEvents) {
          return {
            ...mpAppSession,
            ...eventAppSession,
          };
        } else {
          return mpAppSession;
        }
      },
    );

    return {
      selectUseEvents: (state: RootState) =>
        state.appSessionMP[appSessionId]?.appSession.useEvents ?? false,
      safeSelect,
      select: (state: RootState) => {
        const appSession = safeSelect(state);
        assertNonNull(
          appSession,
          `Can't select non-existent app session of id ${appSessionId}`,
        );
        return appSession;
      },
    };
  }),

  getAppSessionCellSelectors: memoize((appSessionId: AppSessionId) => {
    const baseSelectors = getSelectorsForEntityStateWithSoftDelete(
      appSessionCellAdapter,
      (state) => state.appSessionMP[appSessionId]?.appSessionCells,
    );

    const baseSelectByCellId = (state: RootState, cellId: CellId) => {
      const appSessionCellId =
        state.appSessionMP[appSessionId]?.cellIdToAppSessionCellId[cellId];
      if (appSessionCellId == null) {
        return undefined;
      }
      return baseSelectors.selectById(state, appSessionCellId);
    };

    const baseSelectCellIdToAppSessionCell = createDraftSafeSelector(
      baseSelectors.selectAll,
      (appSessionCells) =>
        Object.fromEntries((appSessionCells ?? []).map((a) => [a.cellId, a])),
    );
    const baseSelectCellIdToAppSessionCellId = (state: RootState) => {
      return state.appSessionMP[appSessionId]?.cellIdToAppSessionCellId ?? {};
    };

    const convertToMP = createCachedConvert(
      (eventAppSessionCell: AppSessionCell): AppSessionCellMP => {
        return {
          __typename: "AppSessionCell" as const,
          revision: 0,
          deletedDate: null,
          ...eventAppSessionCell,
        };
      },
    );

    const selectByIdWithDeleted = createDraftSafeSelector(
      (state: RootState) =>
        appSessionMPSelectors
          .getAppSessionSelectors(appSessionId)
          .selectUseEvents(state),
      baseSelectors.selectByIdWithDeleted,
      appSessionEventStateSelectors(appSessionId).appSessionCells.selectById,
      (useEvents, mpAppSessionCell, eventAppSessionCell) => {
        if (useEvents) {
          if (eventAppSessionCell == null) {
            return eventAppSessionCell;
          } else {
            return convertToMP(eventAppSessionCell);
          }
        } else {
          return mpAppSessionCell;
        }
      },
    );

    return {
      selectById: createDraftSafeSelector(
        selectByIdWithDeleted,
        (appSessionCell) => {
          if (appSessionCell?.deletedDate != null) {
            return null;
          } else {
            return appSessionCell;
          }
        },
      ),
      selectByIdWithDeleted,
      selectAll: createDraftSafeSelector(
        (state: RootState) =>
          appSessionMPSelectors
            .getAppSessionSelectors(appSessionId)
            .selectUseEvents(state),
        baseSelectors.selectAll,
        appSessionEventStateSelectors(appSessionId).appSessionCells.selectArray,
        (useEvents, mpAppSessionCells, eventAppSessionCells) => {
          if (useEvents) {
            return eventAppSessionCells?.map((c) => convertToMP(c)) ?? null;
          } else {
            return mpAppSessionCells;
          }
        },
      ),
      selectEntities: createDraftSafeSelector(
        (state: RootState) =>
          appSessionMPSelectors
            .getAppSessionSelectors(appSessionId)
            .selectUseEvents(state),
        baseSelectors.selectEntities,
        appSessionEventStateSelectors(appSessionId).appSessionCells.select,
        (useEvents, mpAppSessionCells, eventAppSessionCells) => {
          if (useEvents) {
            if (eventAppSessionCells == null) {
              return eventAppSessionCells;
            } else {
              return Object.fromEntries(
                Object.entries(eventAppSessionCells).map(([id, c]) => [
                  id,
                  convertToMP(c),
                ]),
              );
            }
          } else {
            return mpAppSessionCells;
          }
        },
      ),
      selectByCellId: createDraftSafeSelector(
        (state: RootState) =>
          appSessionMPSelectors
            .getAppSessionSelectors(appSessionId)
            .selectUseEvents(state),
        baseSelectByCellId,
        appSessionEventStateSelectors(appSessionId).appSessionCells
          .selectByCellId,
        (useEvents, mpAppSessionCell, eventAppSessionCell) => {
          if (useEvents) {
            if (eventAppSessionCell == null) {
              return eventAppSessionCell;
            } else {
              return convertToMP(eventAppSessionCell);
            }
          } else {
            return mpAppSessionCell;
          }
        },
      ),
      selectCellIdToAppSessionCell: createDraftSafeSelector(
        (state: RootState) =>
          appSessionMPSelectors
            .getAppSessionSelectors(appSessionId)
            .selectUseEvents(state),
        baseSelectCellIdToAppSessionCell,
        appSessionEventStateSelectors(appSessionId).appSessionCells.selectArray,
        (useEvents, mpAppSessionCells, eventAppSessionCells) => {
          if (useEvents) {
            return Object.fromEntries(
              (eventAppSessionCells ?? []).map((c) => [
                c.cellId,
                convertToMP(c),
              ]),
            );
          } else {
            return mpAppSessionCells;
          }
        },
      ),
      selectCellIdToAppSessionCellId: createDraftSafeSelector(
        (state: RootState) =>
          appSessionMPSelectors
            .getAppSessionSelectors(appSessionId)
            .selectUseEvents(state),
        baseSelectCellIdToAppSessionCellId,
        appSessionEventStateSelectors(appSessionId)
          .selectCellIdToAppSessionCellId,
        (
          useEvents,
          mpCellIdToAppSessionCellId,
          eventCellIdToAppSessionCellId,
        ) => {
          if (useEvents) {
            return eventCellIdToAppSessionCellId;
          } else {
            return mpCellIdToAppSessionCellId;
          }
        },
      ),
    };
  }),

  getChartCellSessionStateSelectors: memoize((appSessionId: AppSessionId) => ({
    selectByAppSessionCellId: (
      state: RootState,
      appSessionCellId: AppSessionCellId,
    ) => {
      const data =
        state.appSessionMP[appSessionId]?.chartCellSessionStates.entities[
          appSessionCellId
        ];
      return data == null || data.deletedDate != null ? null : data;
    },
    selectByAppSessionCellIdWithDeleted: (
      state: RootState,
      appSessionCellId: AppSessionCellId,
    ) =>
      state.appSessionMP[appSessionId]?.chartCellSessionStates.entities[
        appSessionCellId
      ] ?? null,
  })),

  getDisplayTableSessionStateSelectors: memoize(
    (appSessionId: AppSessionId) => {
      const baseSelectors = getSelectorsForEntityStateWithSoftDelete(
        displayTableSessionStateAdapter,
        (state) => state.appSessionMP[appSessionId]?.displayTableSessionStates,
      );
      return {
        ...baseSelectors,
        selectByAppSessionCellId: (
          state: RootState,
          appSessionCellId: AppSessionCellId,
        ) => {
          const data =
            state.appSessionMP[appSessionId]?.displayTableSessionStates
              .entities[appSessionCellId];
          return data == null || data.deletedDate != null ? null : data;
        },
        selectByAppSessionCellIdWithDeleted: (
          state: RootState,
          appSessionCellId: AppSessionCellId,
        ) =>
          state.appSessionMP[appSessionId]?.displayTableSessionStates.entities[
            appSessionCellId
          ] ?? null,
        selectHasDisplayTableFilters: (state: RootState) => {
          const data =
            state.appSessionMP[appSessionId]?.displayTableSessionStates
              .entities ?? {};
          for (const displayTableSessionState of Object.values(data)) {
            if (
              displayTableSessionState &&
              displayTableSessionState.deletedDate == null &&
              displayTableSessionState.additionalFilters &&
              displayTableSessionState.additionalFilters.length > 0
            ) {
              return true;
            }
          }
          return false;
        },
        selectConfigIdToDisplayTableFilters: (state: RootState) => {
          const data =
            state.appSessionMP[appSessionId]?.displayTableSessionStates
              .entities ?? {};
          return typedObjectValues(data).reduce<
            Record<DisplayTableConfigId, DisplayTableFilterStates>
          >((acc, displayTableSessionState) => {
            if (
              displayTableSessionState == null ||
              displayTableSessionState.deletedDate != null
            ) {
              return acc;
            }
            const { additionalFilters, displayTableConfigId, id } =
              displayTableSessionState;
            acc[displayTableConfigId] = {
              displayTableSessionStateId: id,
              additionalFilters: additionalFilters,
            };
            return acc;
          }, {});
        },
      };
    },
  ),

  getFilterCellSessionStateSelectors: memoize((appSessionId: AppSessionId) => {
    const baseSelectors = getSelectorsForEntityStateWithSoftDelete(
      filterCellSessionStateAdapter,
      (state) => state.appSessionMP[appSessionId]?.filterCellSessionStates,
    );
    return {
      ...baseSelectors,
      selectByAppSessionCellId: (
        state: RootState,
        appSessionCellId: AppSessionCellId,
      ) => {
        const data =
          state.appSessionMP[appSessionId]?.filterCellSessionStates.entities[
            appSessionCellId
          ];
        return data == null || data.deletedDate != null ? null : data;
      },
      selectByAppSessionCellIdWithDeleted: (
        state: RootState,
        appSessionCellId: AppSessionCellId,
      ) =>
        state.appSessionMP[appSessionId]?.filterCellSessionStates.entities[
          appSessionCellId
        ] ?? null,
      selectHasFilterCellSessionState: (state: RootState) => {
        const data =
          state.appSessionMP[appSessionId]?.filterCellSessionStates.entities ??
          {};
        for (const filterCellSessionState of Object.values(data)) {
          if (
            filterCellSessionState &&
            filterCellSessionState.deletedDate == null &&
            ((filterCellSessionState.filtersOverride &&
              filterCellSessionState.filtersOverride.filters.length > 0) ||
              filterCellSessionState.filterTypeOverride)
          ) {
            return true;
          }
        }
        return false;
      },
    };
  }),

  getExploreSpecSessionStateSelectors: memoize((appSessionId: AppSessionId) => {
    const baseSelectors = getSelectorsForEntityStateWithSoftDelete(
      exploreSpecSessionStateAdapter,
      (state) => state.appSessionMP[appSessionId]?.exploreSpecSessionStates,
    );
    return {
      ...baseSelectors,
      selectByAppSessionCellId: (
        state: RootState,
        appSessionCellId: AppSessionCellId,
      ) => {
        const exploreSpecSessionStateId =
          state.appSessionMP[appSessionId]
            ?.appSessionCellIdToExploreSpecSessionStateId[appSessionCellId];
        if (!exploreSpecSessionStateId) return null;

        const data =
          state.appSessionMP[appSessionId]?.exploreSpecSessionStates.entities[
            exploreSpecSessionStateId
          ];
        return data == null || data.deletedDate != null ? null : data;
      },
      selectByAppSessionCellIdWithDeleted: (
        state: RootState,
        appSessionCellId: AppSessionCellId,
      ) => {
        const exploreSpecSessionStateId =
          state.appSessionMP[appSessionId]
            ?.appSessionCellIdToExploreSpecSessionStateId[appSessionCellId];
        if (!exploreSpecSessionStateId) return null;

        return (
          state.appSessionMP[appSessionId]?.exploreSpecSessionStates.entities[
            exploreSpecSessionStateId
          ] ?? null
        );
      },
    };
  }),

  getSharedFilterSessionStateSelectors: memoize(
    (appSessionId: AppSessionId) => {
      return getSelectorsForEntityStateWithSoftDelete(
        sharedFilterSessionStateAdapter,
        (state) => state.appSessionMP[appSessionId]?.sharedFilterSessionStates,
      );
    },
  ),

  getOutputSelectors: memoize((appSessionId: AppSessionId) => {
    const baseSelectors = getSelectorsForEntityState(
      outputStateAdapter,
      (state) => state.appSessionMP[appSessionId]?.outputs,
    );

    const convertToMPContent = createCachedConvert(
      (eventOutput: OutputContent): OutputContentMP => {
        return {
          __typename: "OutputContent",
          revision: 0,
          ...eventOutput,
        };
      },
    );

    const convertToMP = createCachedConvert(
      ({ outputContents, ...eventOutput }: OutputWithContent): OutputMP => {
        return {
          __typename: "Output",
          revision: 0,
          frontendOutputContents: outputContents.map((c) =>
            convertToMPContent(c),
          ),
          ...eventOutput,
        };
      },
    );

    const selectById = createDraftSafeSelector(
      appSessionMPSelectors.getAppSessionSelectors(appSessionId)
        .selectUseEvents,
      baseSelectors.selectById,
      appSessionEventStateSelectors(appSessionId).outputsWithContent.selectById,
      (useEvents, mpOutput, eventOutput): OutputMP | null | undefined => {
        if (useEvents) {
          if (eventOutput == null) {
            return undefined;
          } else {
            convertToMP(eventOutput);
          }
        } else {
          return mpOutput;
        }
      },
    );

    const selectAll = createDraftSafeSelector(
      (state: RootState) =>
        appSessionMPSelectors
          .getAppSessionSelectors(appSessionId)
          .selectUseEvents(state),
      baseSelectors.selectAll,
      appSessionEventStateSelectors(appSessionId).outputsWithContent
        .selectArray,
      (useEvents, mpOutputs, eventOutputs) => {
        if (useEvents) {
          return eventOutputs?.map((c) => convertToMP(c)) ?? null;
        } else {
          return mpOutputs;
        }
      },
    );

    const selectOutputIdsByAppSessionCellId = createDraftSafeSelector(
      (state: RootState) =>
        appSessionMPSelectors
          .getAppSessionSelectors(appSessionId)
          .selectUseEvents(state),
      (state: RootState, appSessionCellId: AppSessionCellId) =>
        state.appSessionMP[appSessionId]?.appSessionCellIdToOutputIds[
          appSessionCellId
        ],
      (state: RootState, appSessionCellId: AppSessionCellId) =>
        appSessionEventStateSelectors(
          appSessionId,
        ).selectAppSessionCellIdToOutputIds(state)?.[appSessionCellId],
      (useEvents, mpIndex, eventIndex) => {
        if (useEvents) {
          return eventIndex ?? stableEmptyArray<OutputId>();
        } else {
          return mpIndex ?? stableEmptyArray<OutputId>();
        }
      },
    );

    const selectEntities = createDraftSafeSelector(
      (state: RootState) =>
        appSessionMPSelectors
          .getAppSessionSelectors(appSessionId)
          .selectUseEvents(state),
      baseSelectors.selectEntities,
      appSessionEventStateSelectors(appSessionId).outputsWithContent.select,
      (useEvents, mpOutputs, eventOutputs) => {
        if (useEvents) {
          if (eventOutputs == null) return eventOutputs;
          return Object.fromEntries(
            Object.entries(eventOutputs).map(([id, eventOutput]) => [
              id,
              eventOutput == null ? undefined : convertToMP(eventOutput),
            ]),
          );
        } else {
          return mpOutputs;
        }
      },
    );

    // It turns out to be exceedingly tricky to make sure that
    // an output combined with its fully loaded contents always
    // returns a stable value. To get around this, we use several layers
    // of weak maps to make sure we do.

    const stableResolvedOutputContentCache = new WeakMap<
      OutputContentMP,
      WeakMap<OutputContentValue, ResolvedOutputContentMP>
    >();
    const getStableResolvedOutputContent = (
      outputContent: OutputContentMP,
      contentValue: OutputContentValue,
    ): ResolvedOutputContentMP => {
      let innerCache = stableResolvedOutputContentCache.get(outputContent);
      if (innerCache == null) {
        innerCache = new Map();
        stableResolvedOutputContentCache.set(outputContent, innerCache);
      }

      const prevVal = innerCache.get(contentValue);

      if (prevVal != null) {
        return prevVal;
      } else {
        const newVal = {
          ...outputContent,
          ...contentValue,
        };
        innerCache.set(contentValue, newVal);
        return newVal;
      }
    };

    const stableResolvedOutputContentsCache = new WeakMap<
      OutputMP,
      ResolvedOutputContentMP[]
    >();
    const getStableResolvedOutputContents = (
      output: OutputMP,
      contents: ResolvedOutputContentMP[],
    ): ResolvedOutputContentMP[] => {
      const prevVal = stableResolvedOutputContentsCache.get(output);

      if (prevVal != null && shallowEqual(prevVal, contents)) {
        return prevVal;
      } else {
        stableResolvedOutputContentsCache.set(output, contents);
        return contents;
      }
    };

    const stableOutputWithResolvedContentsCache = new WeakMap<
      OutputMP,
      WeakMap<ResolvedOutputContentMP[], OutputMPWithResolvedContent>
    >();
    const getStableOutputWithResolvedContents = (
      output: OutputMP,
      outputContentValues: Record<
        OutputContentId,
        OutputContentValue | undefined
      >,
    ): OutputMPWithResolvedContent => {
      const resolvedOutputContents = output.frontendOutputContents.map((c) =>
        getStableResolvedOutputContent(
          c,
          outputContentValues[c.id] ?? {
            id: c.id,
            status: OutputContentLoadStatus.NO_CONTENT,
            contents: undefined,
          },
        ),
      );
      const stableOutputContents = getStableResolvedOutputContents(
        output,
        resolvedOutputContents,
      );

      let innerCache = stableOutputWithResolvedContentsCache.get(output);
      if (innerCache == null) {
        innerCache = new Map();
        stableOutputWithResolvedContentsCache.set(output, innerCache);
      }

      const prevVal = innerCache.get(stableOutputContents);

      if (prevVal != null) {
        return prevVal;
      } else {
        const newVal = {
          ...output,
          frontendOutputContents: stableOutputContents,
          exportTypesAndStatus: stableOutputContents.every(
            (c) => c.status === "READY",
          )
            ? getExportTypesAndStatus(stableOutputContents)
            : [],
        };
        innerCache.set(stableOutputContents, newVal);
        return newVal;
      }
    };

    const selectEntitiesWithResolvedContent = createSelector(
      selectEntities,
      (state: RootState) =>
        outputContentSelectors.selectAppSessionValueOrThrow(state, appSessionId)
          .outputContents,
      (
        outputs,
        outputContents,
      ): Record<OutputId, OutputMPWithResolvedContent | undefined> =>
        typedObjectFromEntries(
          typedObjectEntries(outputs ?? {}).map(([outputId, output]) => [
            outputId,
            output != null
              ? getStableOutputWithResolvedContents(output, outputContents)
              : undefined,
          ]),
        ),
    );

    const selectAllWithResolvedContent = createSelector(
      selectEntitiesWithResolvedContent,
      (entities) =>
        Object.values(entities ?? {})
          .filter(notEmpty)
          .sort((a, b) => asciiCompare(a.id, b.id)),
    );

    const selectByIdWithResolvedContent = createSelector(
      selectEntitiesWithResolvedContent,
      (_state: RootState, id: OutputId) => id,
      (entities, id) => entities[id],
    );

    const createSelectByAppSessionCellId = (
      appSessionCellId: AppSessionCellId,
    ) =>
      createShallowResultSelector(
        selectEntities,
        (state: RootState) =>
          selectOutputIdsByAppSessionCellId(state, appSessionCellId),
        (outputs, outputIds): OutputMP[] =>
          outputIds
            ?.map((id) => outputs?.[id])
            .filter(notEmpty)
            .sort((a, b) => a.order - b.order) ?? [],
      );

    const createSelectByAppSessionCellIdWithResolvedContent = (
      appSessionCellId: AppSessionCellId,
    ) =>
      createShallowResultSelector(
        selectEntitiesWithResolvedContent,
        (state: RootState) =>
          selectOutputIdsByAppSessionCellId(state, appSessionCellId),
        (outputs, outputIds): OutputMPWithResolvedContent[] => {
          return (
            outputIds
              ?.map((id) => outputs?.[id])
              .filter(notEmpty)
              .sort((a, b) => asciiCompare(a.id, b.id)) ?? []
          );
        },
      );

    const selectAllLoadedWithResolvedContent = createDraftSafeSelector(
      selectAllWithResolvedContent,
      (outputs): OutputMPWithResolvedContent[] =>
        outputs?.filter((output) =>
          output.frontendOutputContents.every(
            (c) => c.status === OutputContentLoadStatus.READY,
          ),
        ) ?? [],
    );

    const selectAllUnloadedWithResolvedContent = createDraftSafeSelector(
      selectAllWithResolvedContent,
      (outputs): OutputMPWithResolvedContent[] =>
        outputs.filter((output) =>
          output.frontendOutputContents.some(
            (c) => c.status === OutputContentLoadStatus.NO_CONTENT,
          ),
        ),
    );

    const selectAllFinishedLoading = createDraftSafeSelector(
      selectAllWithResolvedContent,
      (outputs): boolean =>
        outputs?.every((output) =>
          output.frontendOutputContents.every(
            (c) => c.status !== OutputContentLoadStatus.LOADING,
          ),
        ) ?? true,
    );

    return {
      selectEntities,
      selectEntitiesWithResolvedContent: selectEntitiesWithResolvedContent,
      selectAll,
      selectAllFinishedLoading,
      selectAllWithResolvedContent: selectAllWithResolvedContent,
      selectById,
      selectByIdWithResolvedContent: selectByIdWithResolvedContent,
      createSelectByAppSessionCellId,
      createSelectByAppSessionCellIdWithResolvedContent:
        createSelectByAppSessionCellIdWithResolvedContent,
      selectAllUnloadedWithResolvedContent:
        selectAllUnloadedWithResolvedContent,
      selectAllLoadedWithResolvedContent: selectAllLoadedWithResolvedContent,
    };
  }),

  getKernelSelectors: memoize((appSessionId: AppSessionId) => {
    return {
      // no `select` method because `Kernel` commonly might be `null` and we want to force handling of that
      safeSelect: (state: RootState) =>
        state.appSessionMP[appSessionId]?.kernel ?? null,
    };
  }),

  getParameterValueSelectors: memoize((appSessionId: AppSessionId) => {
    const baseSelectors = getSelectorsForEntityStateWithSoftDelete(
      parameterValueAdapter,
      (state) => state.appSessionMP[appSessionId]?.parameterValues,
    );

    const selectParameterIdToParameterValueId = (state: RootState) =>
      state.appSessionMP[appSessionId]?.parameterIdToParameterValueId ?? {};

    const selectByParameterId = (
      state: RootState,
      parameterId: ParameterId,
    ) => {
      const parameterValueId =
        selectParameterIdToParameterValueId(state)[parameterId];
      if (parameterValueId == null) {
        return undefined;
      }
      return baseSelectors.selectById(state, parameterValueId);
    };

    return {
      ...baseSelectors,
      selectParameterIdToParameterValueId,
      selectByParameterId,
      selectHasParameterValues: (state: RootState) => {
        const data =
          state.appSessionMP[appSessionId]?.parameterValues.entities ?? {};
        return Object.values(data).some(
          (parameterValue) =>
            parameterValue && parameterValue.deletedDate == null,
        );
      },
    };
  }),

  getScopeSelectors: memoize((appSessionId: AppSessionId) => {
    const baseSelectors = getSelectorsForEntityState(
      scopeAdapter,
      (state) => state.appSessionMP[appSessionId]?.scope,
    );

    // rename selector to make it clear that it operators off of the `name` value
    const baseSelectByName = baseSelectors.selectById;
    const baseSelectEntitiesWithHidden = baseSelectors.selectEntities;

    const convertToMP = createCachedConvert(
      (eventScopeItem: ScopeItem): ScopeItemMP => {
        return {
          __typename: "ScopeItem",
          revision: 0,
          ...eventScopeItem,
        };
      },
    );

    const selectEntitiesWithHidden = createDraftSafeSelector(
      (state: RootState) =>
        appSessionMPSelectors
          .getAppSessionSelectors(appSessionId)
          .selectUseEvents(state),
      baseSelectEntitiesWithHidden,
      appSessionEventStateSelectors(appSessionId).scopeItems.selectWithHidden,
      (useEvents, mpScopeItems, eventScopeItems) => {
        if (useEvents) {
          if (eventScopeItems == null) {
            return eventScopeItems;
          } else {
            return Object.fromEntries(
              Object.entries(eventScopeItems).map(([name, s]) => [
                name,
                s == null ? undefined : convertToMP(s),
              ]),
            );
          }
        } else {
          return mpScopeItems;
        }
      },
    );

    const selectEntities = createDraftSafeSelector(
      selectEntitiesWithHidden,
      (entities) =>
        entities == null ? null : omitBy(entities, (value) => value?.isHidden),
    );

    const selectAll = createDraftSafeSelector(
      (state: RootState) =>
        appSessionMPSelectors
          .getAppSessionSelectors(appSessionId)
          .selectUseEvents(state),
      baseSelectors.selectAll,
      appSessionEventStateSelectors(appSessionId).scopeItems.selectArray,
      (useEvents, mpScopeItems, eventScopeItems) => {
        if (useEvents) {
          return eventScopeItems?.map((s) => convertToMP(s)) ?? null;
        } else {
          return mpScopeItems;
        }
      },
    );

    const selectByName = createDraftSafeSelector(
      (state: RootState) =>
        appSessionMPSelectors
          .getAppSessionSelectors(appSessionId)
          .selectUseEvents(state),
      baseSelectByName,
      appSessionEventStateSelectors(appSessionId).scopeItems.selectByName,
      (useEvents, mpScopeItem, eventScopeItem) => {
        if (useEvents) {
          return eventScopeItem != null
            ? convertToMP(eventScopeItem)
            : eventScopeItem;
        } else {
          return mpScopeItem;
        }
      },
    );

    return {
      selectByName,
      selectAll,
      selectEntitiesWithHidden,
      selectEntities,
    };
  }),

  getFileSelectors: memoize((appSessionId: AppSessionId) =>
    getSelectorsForEntityState(
      appSessionFilesAdapter,
      (state) => state.appSessionMP[appSessionId]?.files,
    ),
  ),

  getExploreCellContentsSelectors: memoize((appSessionId: AppSessionId) => {
    const selectWithSessionState = (
      state: RootState,
      cellId: CellId,
      hexVersionId: HexVersionId,
      appSessionCellId?: AppSessionCellId,
      isLogicSession?: boolean,
    ) => {
      if (hexVersionId == null) {
        throw new Error(
          `Missing hexVersionId for explore cell contents selector`,
        );
      }

      const cellContents = hexVersionMPSelectors
        .getCellContentSelectors(hexVersionId)
        .selectByCellId(state, cellId);

      if (cellContents?.__typename !== "ExploreCell") {
        throw new Error(
          `Expected ExploreCell, got ${cellContents?.__typename} for cell: ${cellId}`,
        );
      }

      // For logic sessions, we can return the raw contents without the spec session state merged
      if (isLogicSession ?? true) {
        return cellContents;
      }

      // For published apps, we need to merge the spec session state with the base spec
      const finalAppSessionCellId =
        appSessionCellId ||
        appSessionMPSelectors
          .getAppSessionCellSelectors(appSessionId)
          .selectByCellId(state, cellId)?.id;

      const sessionState = finalAppSessionCellId
        ? appSessionMPSelectors
            .getExploreSpecSessionStateSelectors(appSessionId)
            .selectByAppSessionCellId(state, finalAppSessionCellId)
        : null;

      let mergedSpec = cellContents.spec;
      if (sessionState) {
        mergedSpec = cachedMergeExploreSpecWithSessionState(
          cellContents.spec,
          sessionState,
        );
      }

      return { ...cellContents, spec: mergedSpec };
    };

    const safeSelectWithSessionState = (
      state: RootState,
      cellId: CellId | undefined,
      hexVersionId: HexVersionId | undefined,
      appSessionCellId?: AppSessionCellId,
      isLogicSession?: boolean,
    ) => {
      if (cellId == null || hexVersionId == null) return undefined;

      // Check if cell is an ExploreCell before calling unsafe selector
      const cellContents = hexVersionMPSelectors
        .getCellContentSelectors(hexVersionId)
        .selectByCellId(state, cellId);

      if (cellContents?.__typename !== "ExploreCell") {
        return undefined; // Safe: return undefined for non-ExploreCell cases
      }

      return selectWithSessionState(
        state,
        cellId,
        hexVersionId,
        appSessionCellId,
        isLogicSession,
      );
    };

    const selectSpecWithSessionState = (
      state: RootState,
      cellId: CellId,
      hexVersionId: HexVersionId,
      appSessionCellId?: AppSessionCellId,
      isLogicSession?: boolean,
    ) => {
      return selectWithSessionState(
        state,
        cellId,
        hexVersionId,
        appSessionCellId,
        isLogicSession,
      ).spec;
    };

    const safeSelectSpecWithSessionState = (
      state: RootState,
      cellId: CellId | undefined,
      hexVersionId: HexVersionId | undefined,
      appSessionCellId?: AppSessionCellId,
      isLogicSession?: boolean,
    ) => {
      return safeSelectWithSessionState(
        state,
        cellId,
        hexVersionId,
        appSessionCellId,
        isLogicSession,
      )?.spec;
    };

    return {
      selectWithSessionState,
      selectSpecWithSessionState,
      safeSelectWithSessionState,
      safeSelectSpecWithSessionState,
    };
  }),
};
/* eslint-enable @typescript-eslint/explicit-function-return-type */

//#region full multi-AppSessionMP reducer
type AppSessionMPState = {
  [appSessionId: string]: AppSessionMPValue | undefined;
};

export const appSessionMPActions = {
  ...appSessionMPValueSlice.actions,
};

const allActionTypes = new Set(
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
  Object.values(appSessionMPActions).map((a) => a.type),
);

export const appSessionMPReducer = createReducer<AppSessionMPState>(
  {},
  (builder) =>
    builder.addMatcher(
      (action): action is AppSessionAction<unknown> =>
        allActionTypes.has(action.type),
      (state, action) => {
        state[action.payload.appSessionId] = castDraft(
          appSessionMPValueSlice.reducer(
            state[action.payload.appSessionId],
            action,
          ),
        );
      },
    ),
);
